import { CSSProperties, useState, useEffect, useCallback, useRef } from 'react';
import { navItems, sections } from './data/content';
import Header from './components/Header';
import ParallaxSection from './components/ParallaxSection';
import Footer from './components/Footer';
import OrderButton from './components/OrderButton';
import BackToTopButton from './components/BackToTopButton';

function App() {
  const panelIds = [...sections.map((section) => section.id), 'footer'];
  const [activeSection, setActiveSection] = useState('section1');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement | null>(null);
  const isTransitioningRef = useRef(false);
  const touchStartYRef = useRef<number | null>(null);
  const touchScrollRegionRef = useRef<HTMLElement | null>(null);
  const shouldHideOrderButton = activeSection === 'section4b' || activeSection === 'section6' || activeSection === 'section7';
  const shouldLowerOrderButton = activeSection === 'section6' || activeSection === 'section7';
  const isFooterPanel = activeSection === 'footer';

  const handleSectionChange = useCallback((sectionId: string) => {
    const nextIndex = panelIds.indexOf(sectionId);
    if (nextIndex === -1) return;

    setActiveSection(sectionId);
    setActivePanelIndex(nextIndex);
    setIsMobileMenuOpen(false);
  }, [panelIds]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (isMobileMenuOpen && !target.closest('.sidebar') && !target.closest('.hamburger')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    setActiveSection(panelIds[activePanelIndex]);
  }, [activePanelIndex, panelIds]);

  useEffect(() => {
    const updateHeaderHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0);
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const movePanel = (direction: 1 | -1) => {
      if (isTransitioningRef.current) return;

      setActivePanelIndex((current) => {
        const next = Math.max(0, Math.min(panelIds.length - 1, current + direction));
        if (next === current) return current;

        isTransitioningRef.current = true;
        window.setTimeout(() => {
          isTransitioningRef.current = false;
        }, 700);

        return next;
      });
    };

    const getScrollableRegion = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return null;
      return target.closest<HTMLElement>('.panel-scroll-region');
    };

    const canScrollRegion = (region: HTMLElement, direction: 1 | -1) => {
      const maxScrollTop = region.scrollHeight - region.clientHeight;
      if (maxScrollTop <= 0) return false;

      if (direction > 0) {
        return region.scrollTop < maxScrollTop - 1;
      }

      return region.scrollTop > 1;
    };

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 12) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const scrollRegion = getScrollableRegion(event.target);
      if (scrollRegion && canScrollRegion(scrollRegion, direction)) {
        return;
      }

      event.preventDefault();
      movePanel(direction);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault();
        movePanel(1);
      }

      if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        movePanel(-1);
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
      touchScrollRegionRef.current = getScrollableRegion(event.target);
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (touchStartYRef.current === null) return;

      const endY = event.changedTouches[0]?.clientY ?? touchStartYRef.current;
      const deltaY = touchStartYRef.current - endY;
      const scrollRegion = touchScrollRegionRef.current;
      touchStartYRef.current = null;
      touchScrollRegionRef.current = null;

      if (Math.abs(deltaY) < 50) return;

      if (scrollRegion && canScrollRegion(scrollRegion, deltaY > 0 ? 1 : -1)) {
        return;
      }

      movePanel(deltaY > 0 ? 1 : -1);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [panelIds.length]);

  const appStyle = {
    '--header-height': `${headerHeight}px`,
    '--panel-index': activePanelIndex
  } as CSSProperties;

  return (
    <div className="app" style={appStyle}>
      <Header
        headerRef={headerRef}
        navItems={navItems}
        activeSection={activeSection}
        isMobileMenuOpen={isMobileMenuOpen}
        onSectionChange={handleSectionChange}
        onToggleMobileMenu={toggleMobileMenu}
      />

      <main className="panel-track">
        {sections.map(section => (
          <ParallaxSection
            key={section.id}
            section={section}
            isActive={activeSection === section.id}
          />
        ))}

        <section className="panel footer-panel" aria-hidden={activeSection !== 'footer'}>
          <Footer />
        </section>
      </main>

      <OrderButton
        hidden={shouldHideOrderButton}
        lowered={shouldLowerOrderButton}
        leftAligned={isFooterPanel}
      />
      {isFooterPanel ? (
        <BackToTopButton
          show={true}
          onClick={() => handleSectionChange('section1')}
        />
      ) : null}
    </div>
  );
}

export default App;
